import { IconButton } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { IoEyeOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';

import LightTooltip from '@/components/atoms/LightTooltip';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import TypeOfChange from '@/components/organisms/TypeOfChangeModal';
import api from '@/services/apiSgft';
import { pendingNotificationsCountAtom } from '@/state/PendingNotificationsCount.atom';

import TableCellText from '../../components/atoms/TableCellText';
import { removeTimeZone } from '../../utils/formatDate';
import {
	EmployeesChanges,
	NotificationStatusEnum,
} from '../EmployeesChangesConstants';

export const columns: Array<ColumnDef<EmployeesChanges>> = [
	{
		accessorKey: 'status',
		header: 'STATUS',
		meta: {
			headerClassName: 'pl-3 text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-24 pl-3">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'modificationTimestamp',
		header: 'DATA',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-32">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'employeeName',
		header: 'NOME',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-32">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
						isName={true}
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'employeeNumber',
		header: 'CHAPA',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-24">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'training',
		header: 'TREINAMENTO',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-48 pr-2">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'trainingFileId',
		header: 'FICHEIRO',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-24">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'trainingPlanningId',
		header: 'TURMA',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-24">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'trainingPlanningDate',
		header: 'DATA DO TREINAMENTO',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			const dates = value.getValue() as string[];
			const formatDates = (dates: string[]): string => {
				return dates
					.map((date) => {
						const parsedDate = removeTimeZone(new Date(date));
						const day = parsedDate.getDate().toString().padStart(2, '0');
						const month = (parsedDate.getMonth() + 1)
							.toString()
							.padStart(2, '0');
						return `${day}/${month}`;
					})
					.join(', ');
			};

			const formattedDates = formatDates(dates);
			return (
				<div className="w-24">
					<TableCellText
						text={formattedDates as string}
						width="full"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'changeType',
		header: 'TIPO DE MUDANÇA',
		meta: {
			headerClassName: 'pl-4 text-[11px]',
		},
		cell: (value) => {
			const [isModalOpen, setIsModalOpen] = useState(false);
			return (
				<div className="w-40 pl-4" onClick={() => setIsModalOpen(true)}>
					{isModalOpen && (
						<TypeOfChange
							handleClose={() => setIsModalOpen(false)}
							row={value.row.original}
						/>
					)}
					<TableCellText
						text={value.getValue() as string}
						width="full"
						className="cursor-pointer text-[11px] underline decoration-solid"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'necessaryTraining',
		header: 'TREINAMENTO NECESSÁRIO?',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (value) => {
			return (
				<div className="w-36">
					<TableCellText
						text={value.getValue() as string}
						width="full"
						textAlign="text-center"
						className="text-[11px]"
					/>
				</div>
			);
		},
	},
	{
		accessorKey: 'trainingDetail',
		header: 'DETALHAMENTO DE TURMA',
		meta: {
			headerClassName: 'text-[11px]',
		},
		cell: (props) => {
			const trainingFileId = props.row.original.trainingFileId;
			const trainingPlanningId = props.row.original.trainingPlanningId;
			return (
				<div className="flex h-12 w-24 items-center justify-center">
					<Link
						to={`detalhamento/${trainingFileId}/?turma=${trainingPlanningId}`}
						className="flex h-6 w-6 items-center justify-center rounded-full bg-primary hover:bg-primary/70"
					>
						<FaArrowUpRightFromSquare color="white" size={12} />
					</Link>
				</div>
			);
		},
	},
	{
		accessorKey: 'acknowledged',
		header: 'CIENTE?',
		meta: {
			headerClassName: 'pl-3 text-[11px]',
		},
		cell: (props) => {
			const [_, setPendingNotificationsCount] = useRecoilState(
				pendingNotificationsCountAtom,
			);

			const [isAware, setIsAware] = useState(props.row.original.acknowledged);
			const [isModalOpen, setIsModalOpen] = useState(false);

			const confirmAcknowledgment = async () => {
				const id = props.row.original.id;

				try {
					const response = await api.put(
						`/training-modification-notification/acknowledge/${id}`,
					);

					const { data } = response;
					if (!data || !data.email || !data.timestamp) {
						throw new Error('Invalid response data');
					}

					setPendingNotificationsCount((prevCount) =>
						prevCount !== undefined && prevCount > 0 ? prevCount - 1 : 0,
					);

					props.row.original.acknowledged = true;
					props.row.original.acknowledgedBy = data.email;
					props.row.original.acknowledgedAt = data.timestamp;
					props.row.original.status = NotificationStatusEnum.ACKNOWLEDGED;

					setIsAware(true);

					toast.success('Ciência confirmada com sucesso', {
						theme: 'colored',
						toastId: 'success',
					});
				} catch (error) {
					toast.error('Erro ao confirmar ciência', {
						theme: 'colored',
						toastId: 'error',
					});

					console.error(`Error acknowledging item of ID ${id}:`, error);
				} finally {
					setIsModalOpen(false);
				}
			};

			return (
				<div className="flex w-24 items-start justify-start pl-3">
					<ConfirmationModal
						title="Confirmação de Ciência"
						description="Ao prosseguir com esta ação, a ação constará como analisada na aba de Alterações de Colaboradores"
						isOpen={isModalOpen}
						onClose={() => setIsModalOpen(false)}
						confirmAction={confirmAcknowledgment}
					/>
					{isAware ? (
						<LightTooltip
							title={
								<div
									style={{
										whiteSpace: 'pre-line',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'space-between',
										padding: '8px',
									}}
								>
									<div>Usuário: {props.row.original.acknowledgedBy}</div>
									<div>Data: {props.row.original.acknowledgedAt}</div>
								</div>
							}
							placement="top"
							arrow
						>
							<IconButton
								sx={{
									height: '2rem',
									width: '2rem',
									borderRadius: '100%',
									'&:hover': {
										background: '#D2D6DA',
									},
								}}
								aria-label="export"
							>
								<IoEyeOutline color="black" />
							</IconButton>
						</LightTooltip>
					) : (
						<IconButton
							sx={{
								height: '2rem',
								width: '2rem',
								borderRadius: '100%',
								'&:hover': {
									background: '#D2D6DA',
								},
							}}
							aria-label="export"
							onClick={() => {
								setIsModalOpen(true);
							}}
						>
							<FaCheckCircle style={{ color: 'green' }} size={18} />
						</IconButton>
					)}
				</div>
			);
		},
	},
];
